// S23 對話稽核 /ai/conversations — AI 客服/管家對話 traces 唯讀下鑽。
//
// 篩選(status/feedback/q)→ traces 列表(新到舊,≤500;可「載入更早」用 before 續拉)→
// 點列開右側抽屜看下鑽:問句 → 檢索片段+分數 → 回覆+來源 → tokens/延遲/model + 評價 + 同對話各輪。
// 全唯讀(此頁無寫入)。三態一律走 StateBlock。

import { useCallback, useEffect, useRef, useState } from 'react';
import { EmptyState, ErrorState, LoadingState } from '../../components/StateBlock';
import { CopyableId } from '../../components/CopyableId';
import {
  fetchTraceDetail,
  fetchTraces,
  formatCsTime,
  formatLatency,
  ratingLabel,
  ratingTone,
  traceStatusLabel,
  traceStatusTone,
} from './aiOpsApi';
import type { AiTraceDetail, AiTraceRow } from './aiOpsApi';
import '../../features/_shared/shared.css';
import './aiOps.css';

const STATUS_OPTIONS = [
  { value: '', label: '全部狀態' },
  { value: 'answered', label: '已回答' },
  { value: 'no_answer', label: '查無知識(轉人工)' },
  { value: 'out_of_scope', label: '超出範圍' },
  { value: 'error', label: '錯誤' },
];

const FEEDBACK_OPTIONS = [
  { value: '', label: '全部評價' },
  { value: 'up', label: '👍 正評' },
  { value: 'down', label: '👎 負評' },
];

const PAGE = 200;

export default function ConversationsPage() {
  const [status, setStatus] = useState('');
  const [feedback, setFeedback] = useState('');
  const [qInput, setQInput] = useState('');
  const [q, setQ] = useState('');

  const [rows, setRows] = useState<AiTraceRow[] | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState<unknown>(null); // 分頁失敗獨立於頁級 error(否則被 rows!==null 擋住永不顯示)
  const [exhausted, setExhausted] = useState(false);

  const [openId, setOpenId] = useState<string | null>(null);

  // debounce 問句關鍵字(300ms)
  useEffect(() => {
    const t = setTimeout(() => setQ(qInput.trim()), 300);
    return () => clearTimeout(t);
  }, [qInput]);

  const load = useCallback(
    (signal?: AbortSignal) => {
      setLoading(true);
      setError(null);
      setMoreError(null); // 換篩選重載時清掉上一輪分頁殘留錯誤
      fetchTraces({ status, feedback, q, limit: PAGE }, signal)
        .then((r) => {
          setRows(r.traces);
          setExhausted(r.traces.length < PAGE);
        })
        .catch((e) => {
          if (!signal?.aborted) {
            setError(e);
            setRows(null);
          }
        })
        .finally(() => {
          if (!signal?.aborted) setLoading(false);
        });
    },
    [status, feedback, q]
  );

  useEffect(() => {
    const ac = new AbortController();
    load(ac.signal);
    return () => ac.abort();
  }, [load]);

  const loadMore = () => {
    if (!rows || rows.length === 0) return;
    const before = rows[rows.length - 1].created_at;
    setLoadingMore(true);
    setMoreError(null);
    fetchTraces({ status, feedback, q, before, limit: PAGE })
      .then((r) => {
        setRows((prev) => [...(prev ?? []), ...r.traces]);
        setExhausted(r.traces.length < PAGE);
      })
      .catch((e) => setMoreError(e)) // 已有列時失敗:走 pager 旁 inline 錯誤,不動頁級 error
      .finally(() => setLoadingMore(false));
  };

  const hasFilter = !!(status || feedback || q);
  const showInitialLoading = loading && rows === null;

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">對話稽核</h1>
          <p className="page-subtitle">
            AI 客服(concierge)與管家(butler / Aura)的對話軌跡唯讀下鑽:問句 → 檢索片段+分數 → 回覆 → tokens／延遲＋評價。經 restful <span className="aiops-code">/v3/admin/ai/*</span> 代理 cs 治理端點。
          </p>
        </div>
        <button className="btn-ghost" onClick={() => load()} disabled={loading}>
          {loading ? '載入中…' : '重新整理'}
        </button>
      </div>

      <div className="filter-bar is-sticky" data-testid="conversations-filters">
        <label className="adm-field">
          <span className="text-secondary adm-field-label">狀態</span>
          <select value={status} onChange={(e) => setStatus(e.target.value)}>
            {STATUS_OPTIONS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </label>
        <label className="adm-field">
          <span className="text-secondary adm-field-label">評價</span>
          <select value={feedback} onChange={(e) => setFeedback(e.target.value)}>
            {FEEDBACK_OPTIONS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </label>
        <label className="adm-field">
          <span className="text-secondary adm-field-label">問句關鍵字</span>
          <input type="search" placeholder="搜尋問句…" aria-label="依問句關鍵字篩選" value={qInput} onChange={(e) => setQInput(e.target.value)} />
        </label>
        <div className="adm-toolbar-spacer" />
        <button
          className="btn-ghost"
          disabled={!hasFilter}
          onClick={() => {
            setStatus('');
            setFeedback('');
            setQInput('');
          }}
        >
          清除篩選
        </button>
      </div>

      {showInitialLoading && <LoadingState label="載入對話清單…" />}

      {error != null && rows === null && <ErrorState error={error} onRetry={() => load()} />}

      {rows && rows.length === 0 && (
        <EmptyState
          title="沒有符合條件的對話"
          detail={hasFilter ? '試著放寬狀態／評價／關鍵字篩選。' : '目前尚無對話 traces。'}
          action={
            hasFilter ? (
              <button
                className="btn-ghost btn-sm"
                onClick={() => {
                  setStatus('');
                  setFeedback('');
                  setQInput('');
                }}
              >
                清除篩選
              </button>
            ) : undefined
          }
        />
      )}

      {rows && rows.length > 0 && (
        <div className="table-wrap" data-testid="conversations-table">
          <table className="data-table">
            <thead>
              <tr>
                <th>時間</th>
                <th>問句</th>
                <th>狀態</th>
                <th>評價</th>
                <th>延遲</th>
                <th>對話</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((t) => (
                <tr
                  key={t.id}
                  className="clickable"
                  tabIndex={0}
                  onClick={() => setOpenId(t.id)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') setOpenId(t.id);
                  }}
                >
                  <td className="adm-mono">{formatCsTime(t.created_at)}</td>
                  <td className="adm-cell-strong aiops-cell-query" title={t.query}>
                    {t.query || '—'}
                  </td>
                  <td>
                    <span className={`pill ${traceStatusTone(t.status)}`}>{traceStatusLabel(t.status)}</span>
                  </td>
                  <td>
                    {t.rating ? <span className={`pill ${ratingTone(t.rating)}`}>{ratingLabel(t.rating)}</span> : <span className="aiops-muted">—</span>}
                  </td>
                  <td className="adm-mono">{formatLatency(t.latency_ms)}</td>
                  <td>
                    <CopyableId value={t.conversation_id} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="adm-pager">
            <span className="text-secondary">本頁 {rows.length} 筆</span>
            {!exhausted && (
              <button className="btn-ghost btn-sm" onClick={loadMore} disabled={loadingMore}>
                {loadingMore ? '載入中…' : '載入更早'}
              </button>
            )}
            {moreError != null && (
              <span className="error-text" role="alert">
                載入更早失敗
                <button className="btn-ghost btn-sm" style={{ marginLeft: 'var(--spacing-2)' }} onClick={loadMore}>
                  重試
                </button>
              </span>
            )}
          </div>
        </div>
      )}

      {openId && <TraceDrawer id={openId} onClose={() => setOpenId(null)} onOpenTurn={(id) => setOpenId(id)} />}
    </>
  );
}

// ── 下鑽抽屜 ──────────────────────────────────────────────────────────────────

function TraceDrawer({ id, onClose, onOpenTurn }: { id: string; onClose: () => void; onOpenTurn: (id: string) => void }) {
  const [detail, setDetail] = useState<AiTraceDetail | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(false);
  const [nonce, setNonce] = useState(0);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const ac = new AbortController();
    setLoading(true);
    setError(null);
    setDetail(null);
    fetchTraceDetail(id, ac.signal)
      .then(setDetail)
      .catch((e) => {
        if (!ac.signal.aborted) setError(e);
      })
      .finally(() => {
        if (!ac.signal.aborted) setLoading(false);
      });
    return () => ac.abort();
  }, [id, nonce]);

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="aiops-drawer-scrim" role="presentation" onClick={onClose}>
      <aside className="aiops-drawer" role="dialog" aria-modal="true" aria-label="對話下鑽" onClick={(e) => e.stopPropagation()} data-testid="trace-drawer">
        <div className="aiops-drawer-head">
          <h2 className="aiops-drawer-title">
            對話下鑽
            {detail && <span className={`pill ${traceStatusTone(detail.reply?.status)}`}>{traceStatusLabel(detail.reply?.status)}</span>}
          </h2>
          <button ref={closeRef} className="btn-ghost btn-sm aiops-drawer-close" onClick={onClose} aria-label="關閉">
            關閉
          </button>
        </div>

        {loading && <LoadingState label="載入對話詳情…" />}
        {error != null && <ErrorState error={error} onRetry={() => setNonce((n) => n + 1)} />}

        {detail && (
          <>
            <div className="aiops-block">
              <span className="aiops-block-label">問句</span>
              <p className="aiops-quote">{detail.query || '—'}</p>
            </div>

            <div className="aiops-block">
              <span className="aiops-block-label">檢索片段（含相似度分數）</span>
              {detail.retrieved && detail.retrieved.length > 0 ? (
                <div className="aiops-hits">
                  {detail.retrieved.map((h, i) => {
                    const pct = Math.max(0, Math.min(100, Math.round((h.score ?? 0) * 100)));
                    return (
                      <div className="aiops-hit" key={i}>
                        <div>
                          <div className="aiops-hit-source">{h.source || '—'}</div>
                          {h.heading_path && <div className="aiops-hit-heading">{h.heading_path}</div>}
                        </div>
                        <div className="aiops-hit-score">{h.score != null ? h.score.toFixed(3) : '—'}</div>
                        <div className="aiops-scorebar">
                          <span style={{ width: `${pct}%` }} />
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <p className="aiops-hint">無檢索片段（可能為超出範圍或工具直答）。</p>
              )}
            </div>

            <div className="aiops-block">
              <span className="aiops-block-label">回覆</span>
              <p className="aiops-quote aiops-answer">{detail.reply?.answer || '—'}</p>
              {detail.reply?.sources && detail.reply.sources.length > 0 && (
                <ul className="aiops-hits" style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {detail.reply.sources.map((s, i) => (
                    <li key={i} className="aiops-hit">
                      <div className="aiops-hit-source">{s.title || s.url}</div>
                      <div className="aiops-hit-heading" style={{ gridColumn: '1 / -1' }}>
                        {s.url}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div className="aiops-block">
              <span className="aiops-block-label">度量</span>
              <div className="aiops-meta-grid">
                <div className="aiops-meta-item">
                  <span className="aiops-meta-k">模型</span>
                  <span className="aiops-meta-v">{detail.model || '—'}</span>
                </div>
                <div className="aiops-meta-item">
                  <span className="aiops-meta-k">用量</span>
                  <span className="aiops-meta-v">{detail.tokens ?? '—'}</span>
                </div>
                <div className="aiops-meta-item">
                  <span className="aiops-meta-k">延遲</span>
                  <span className="aiops-meta-v">{formatLatency(detail.latency_ms)}</span>
                </div>
                <div className="aiops-meta-item">
                  <span className="aiops-meta-k">時間</span>
                  <span className="aiops-meta-v">{formatCsTime(detail.created_at)}</span>
                </div>
              </div>
            </div>

            <div className="aiops-block">
              <span className="aiops-block-label">評價</span>
              {detail.feedback ? (
                <div>
                  <span className={`pill ${ratingTone(detail.feedback.rating)}`}>{ratingLabel(detail.feedback.rating)}</span>
                  {detail.feedback.comment && <p className="aiops-quote" style={{ marginTop: 'var(--spacing-2)' }}>{detail.feedback.comment}</p>}
                  <div className="aiops-hint" style={{ marginTop: 4 }}>{formatCsTime(detail.feedback.created_at)}</div>
                </div>
              ) : (
                <p className="aiops-hint">尚無評價。</p>
              )}
            </div>

            {detail.conversation_turns.length > 1 && (
              <div className="aiops-block">
                <span className="aiops-block-label">同對話各輪（{detail.conversation_turns.length}）</span>
                <div className="aiops-turns">
                  {detail.conversation_turns.map((turn) => {
                    const current = turn.id === detail.id;
                    return (
                      <button
                        key={turn.id}
                        className={`aiops-turn${current ? ' is-current' : ''}`}
                        disabled={current}
                        onClick={() => !current && onOpenTurn(turn.id)}
                      >
                        <span className={`pill ${traceStatusTone(turn.status)}`}>{traceStatusLabel(turn.status)}</span>
                        <span className="aiops-turn-q" title={turn.query}>
                          {turn.query || '—'}
                        </span>
                        <span className="aiops-meta-k">{formatCsTime(turn.created_at)}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </aside>
    </div>
  );
}
